import {
  useCallback,
  useEffect,
  useState,
} from "react";
import axios from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  Upload,
} from "lucide-react";

import api from "../../../lib/api";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import {
  Table,
  type TableColumn,
} from "../../../components/ui/Table";

interface ImportacionEstado {
  clientes: number;
  polizas: number;
  importacionRealizada: boolean;
}

interface ImportError {
  fila: number;
  campo: string;
  error: string;
}

interface ImportWarning {
  fila: number;
  aviso: string;
}

interface ImportRow {
  fila: number;
  idCliente: string;
  cliente: string;
  idPoliza: string;
  tipoPoliza: string;
  numeroPoliza: string;
  fechaEfecto: string;
  fechaVencimiento: string;
}

interface ImportPreview {
  total: number;
  validas: number;
  errores: ImportError[];
  avisos: ImportWarning[];
  puedeConfirmar: boolean;
  filas: ImportRow[];
  importadas?: number;
}

function getErrorMessage(error: unknown): string {
  if (
    axios.isAxiosError<
      ImportPreview & { error?: string }
    >(error)
  ) {
    return (
      error.response?.data?.error ??
      "No se pudo completar la operación"
    );
  }

  return "No se pudo completar la operación";
}

function formatDate(value: string): string {
  const parts = value.split("-");

  if (parts.length !== 3) return value;

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export function CrmImportacionTab() {
  const [estado, setEstado] =
    useState<ImportacionEstado | null>(null);
  const [estadoLoading, setEstadoLoading] =
    useState(true);

  const [file, setFile] = useState<File | null>(
    null
  );
  const [preview, setPreview] =
    useState<ImportPreview | null>(null);

  const [checking, setChecking] = useState(false);
  const [confirmOpen, setConfirmOpen] =
    useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadEstado = useCallback(async () => {
    setEstadoLoading(true);

    try {
      const response =
        await api.get<ImportacionEstado>(
          "/admin/crm/importacion/estado"
        );

      setEstado(response.data);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setEstadoLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEstado();
  }, [loadEstado]);

  const selectFile = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selected =
      event.target.files?.[0] ?? null;

    setPreview(null);
    setError("");
    setSuccess("");

    if (!selected) {
      setFile(null);
      return;
    }

    if (
      !selected.name.toLowerCase().endsWith(".xlsx")
    ) {
      setFile(null);
      setError(
        "Selecciona un archivo Excel con formato .xlsx"
      );
      event.target.value = "";
      return;
    }

    if (selected.size > 2 * 1024 * 1024) {
      setFile(null);
      setError(
        "El archivo supera el tamaño máximo de 2 MB"
      );
      event.target.value = "";
      return;
    }

    setFile(selected);
  };

  const sendFile = async (
    confirmar: boolean
  ): Promise<ImportPreview> => {
    if (!file) {
      throw new Error(
        "Selecciona primero el archivo Excel"
      );
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "confirmar",
      confirmar ? "true" : "false"
    );

    const response =
      await api.post<ImportPreview>(
        "/admin/crm/importacion",
        formData
      );

    return response.data;
  };

  const checkFile = async () => {
    if (!file) {
      setError(
        "Selecciona primero el archivo Excel"
      );
      return;
    }

    setChecking(true);
    setError("");
    setSuccess("");
    setPreview(null);

    try {
      const result = await sendFile(false);
      setPreview(result);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setChecking(false);
    }
  };

  const confirmImport = async () => {
    setError("");
    setSuccess("");

    try {
      const result = await sendFile(true);

      setPreview(result);
      setSuccess(
        `Importación completada: ${
          result.importadas ?? result.validas
        } pólizas incorporadas.`
      );

      await loadEstado();
    } catch (requestError) {
      if (
        axios.isAxiosError<ImportPreview>(
          requestError
        ) &&
        requestError.response?.data?.errores
      ) {
        setPreview(requestError.response.data);
      }

      setError(getErrorMessage(requestError));
    }
  };

  const previewColumns: TableColumn<ImportRow>[] = [
    {
      key: "fila",
      header: "Fila",
    },
    {
      key: "idCliente",
      header: "ID cliente",
    },
    {
      key: "cliente",
      header: "Cliente",
    },
    {
      key: "idPoliza",
      header: "ID póliza",
    },
    {
      key: "tipoPoliza",
      header: "Tipo",
    },
    {
      key: "numeroPoliza",
      header: "Nº póliza",
    },
    {
      key: "fechaEfecto",
      header: "Fecha efecto",
      render: (row) =>
        formatDate(row.fechaEfecto),
    },
    {
      key: "fechaVencimiento",
      header: "Vencimiento",
      render: (row) =>
        formatDate(row.fechaVencimiento),
    },
  ];

  if (estadoLoading) {
    return (
      <div className="py-10 text-center text-sm text-gray-500">
        Comprobando el estado de la importación…
      </div>
    );
  }

  if (estado?.importacionRealizada) {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Importación inicial
          </h2>

          <p className="mt-1 text-sm text-gray-600">
            El Excel inicial ya ha sido incorporado.
          </p>
        </div>

        <div className="rounded-lg border border-green-200 bg-green-50 p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-600" />

            <div>
              <p className="font-semibold text-green-900">
                Importación realizada
              </p>

              <p className="mt-1 text-sm text-green-800">
                Clientes CRM: {estado.clientes}
                {" · "}
                Pólizas CRM: {estado.polizas}
              </p>

              <p className="mt-2 text-sm text-green-800">
                Para evitar duplicados, esta pantalla ya no
                permite volver a importar el Excel.
              </p>
            </div>
          </div>
        </div>

        {success && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            {success}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Importación inicial
        </h2>

        <p className="mt-1 text-sm text-gray-600">
          Esta herramienta se utilizará una sola vez para
          incorporar los clientes y pólizas del Excel.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />

          <div className="text-sm text-amber-900">
            <p className="font-semibold">
              Primero se revisará el archivo
            </p>

            <p className="mt-1">
              La comprobación no guarda datos. La importación
              solo se realizará después de revisar el
              resultado y pulsar el botón de confirmación.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center">
        <FileSpreadsheet className="mx-auto h-10 w-10 text-gray-400" />

        <p className="mt-3 text-sm font-medium text-gray-900">
          Selecciona el Excel inicial
        </p>

        <p className="mt-1 text-xs text-gray-500">
          Solo archivos .xlsx, con un máximo de 2 MB
        </p>

        <label className="mt-4 inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
          <Upload className="h-4 w-4" />
          Elegir archivo

          <input
            type="file"
            accept=".xlsx"
            onChange={selectFile}
            className="sr-only"
          />
        </label>

        {file && (
          <p className="mt-3 text-sm text-[#1A3AB8]">
            Archivo seleccionado: {file.name}
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          {success}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={checkFile}
          loading={checking}
          disabled={!file}
        >
          Revisar Excel
        </Button>
      </div>

      {preview && (
        <div className="space-y-5 border-t border-gray-200 pt-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-sm text-gray-500">
                Filas encontradas
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {preview.total}
              </p>
            </div>

            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="text-sm text-green-700">
                Filas válidas
              </p>
              <p className="mt-1 text-2xl font-bold text-green-900">
                {preview.validas}
              </p>
            </div>

            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">
                Errores
              </p>
              <p className="mt-1 text-2xl font-bold text-red-900">
                {preview.errores.length}
              </p>
            </div>
          </div>

          {preview.errores.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="font-semibold text-red-900">
                Errores que debes corregir
              </p>

              <div className="mt-3 space-y-2">
                {preview.errores.map(
                  (item, index) => (
                    <div
                      key={`${item.fila}-${item.campo}-${index}`}
                      className="text-sm text-red-800"
                    >
                      Fila {item.fila} · {item.campo}:{" "}
                      {item.error}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {preview.avisos.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="font-semibold text-amber-900">
                Avisos para revisar
              </p>

              <div className="mt-3 space-y-2">
                {preview.avisos.map(
                  (item, index) => (
                    <div
                      key={`${item.fila}-${index}`}
                      className="text-sm text-amber-800"
                    >
                      Fila {item.fila}: {item.aviso}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-semibold text-gray-900">
                Datos preparados
              </h3>

              <Badge
                variant={
                  preview.puedeConfirmar
                    ? "success"
                    : "danger"
                }
              >
                {preview.puedeConfirmar
                  ? "Preparado para importar"
                  : "Requiere correcciones"}
              </Badge>
            </div>

            <Table
              columns={previewColumns}
              data={preview.filas}
              emptyMessage="No hay filas válidas para mostrar"
              floatingHorizontalScrollbar
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!preview.puedeConfirmar}
            >
              Confirmar importación
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmImport}
        title="Confirmar importación inicial"
        message="Se crearán los clientes y pólizas del Excel en la nueva cartera CRM. Esta operación no modificará las pólizas operativas actuales. ¿Quieres continuar?"
        confirmLabel="Sí, importar"
      />
    </div>
  );
}